import { Box, Button } from "@mui/material";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { type FormEvent, useState } from "react";

interface LoginFormProps {
  email: string;
  setEmail: (email: string) => void;
  loading: boolean;
  onSubmit: () => Promise<void>;
  onSwitchToPassword: () => void;
  onSsoClick?: () => Promise<void>;
  showSsoButton?: boolean;
  isSsoEmail?: boolean;
  verifyingEmail?: boolean;
  disabled?: boolean;
}

/**
 * Login form with magic link
 */
export function LoginForm(props: LoginFormProps) {
  const {
    email,
    setEmail,
    loading,
    onSubmit,
    onSwitchToPassword,
    onSsoClick,
    showSsoButton = false,
    isSsoEmail = false,
    verifyingEmail = false,
    disabled = false,
  } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);

  const formLabel = verifyingEmail
    ? "Please enter your email id again to verify login link"
    : "Enter your email to get started";

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  }

  function getSubmitLabel(): string {
    if (verifyingEmail) {
      return "Verify Email";
    }

    if (isSsoEmail) {
      return "Sign in with SSO";
    }

    return "Get Sign-in Link";
  }

  const submitLabel = getSubmitLabel();

  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Text variant="body1" sx={{ textAlign: "center", color: "base.muted" }}>
        {formLabel}
      </Text>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Text semibold variant="body2" sx={{ color: "base.muted" }}>
          Email
        </Text>
        <TextInput
          fullWidth
          required
          type="email"
          placeholder="your.email@example.com"
          value={email}
          disabled={disabled || loading}
          autoComplete="email"
          inputProps={{ "aria-label": "Email" }}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "1rem",
              backgroundColor: "background.standout",
            },
          }}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
      </Box>

      {showSsoButton ? (
        <Box sx={{ display: "flex", gap: 2 }}>
          <LoadingButton
            fullWidth
            type="submit"
            variant="primary"
            size="large"
            isLoading={isSubmitting || loading}
            disabled={disabled}
          >
            Get Sign-in Link
          </LoadingButton>
          <LoadingButton
            fullWidth
            variant="primary"
            size="large"
            isLoading={isSsoLoading}
            disabled={disabled || isSubmitting || loading || isSsoLoading}
            onClick={(event) => {
              event.preventDefault();
              setIsSsoLoading(true);
              onSsoClick?.()?.finally(() => {
                setIsSsoLoading(false);
              });
            }}
          >
            Sign in with SSO
          </LoadingButton>
        </Box>
      ) : (
        <LoadingButton
          fullWidth
          type="submit"
          variant="primary"
          size="large"
          isLoading={isSubmitting || loading}
          disabled={disabled}
        >
          {submitLabel}
        </LoadingButton>
      )}

      {/* Divider */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "border.base" }} />
        <Text variant="body2" sx={{ color: "base.inactive" }}>
          or
        </Text>
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "border.base" }} />
      </Box>

      {/* Alternative login option */}
      <Button
        variant="text"
        disabled={disabled || loading}
        sx={{
          textTransform: "none",
          fontSize: "1rem",
          color: "primary.main",
          alignSelf: "center",
        }}
        onClick={onSwitchToPassword}
      >
        Sign in with email & password
      </Button>
    </Box>
  );
}
